import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getDocumentIndexState } from '../services/documents';
import type { DocumentIndexState } from '../types/documents';

interface DocumentIndexingContextValue {
  states: Record<number, DocumentIndexState>;
  watch: (nodeIds: number[]) => () => void;
}

const DocumentIndexingContext = createContext<DocumentIndexingContextValue | null>(null);
const TERMINAL = new Set(['indexed', 'unsupported', 'extraction_failed', 'too_large', 'failed']);

export function DocumentIndexingProvider({ children }: { children: ReactNode }) {
  const [states, setStates] = useState<Record<number, DocumentIndexState>>({});
  const statesRef = useRef(states);
  statesRef.current = states;
  const watched = useRef(new Map<number, number>());

  const watch = useCallback((nodeIds: number[]) => {
    nodeIds.forEach((id) => watched.current.set(id, (watched.current.get(id) ?? 0) + 1));
    return () => nodeIds.forEach((id) => {
      const count = (watched.current.get(id) ?? 1) - 1;
      if (count <= 0) watched.current.delete(id); else watched.current.set(id, count);
    });
  }, []);

  useEffect(() => {
    let stopped = false;
    let inFlight = false;
    const poll = async () => {
      if (stopped || inFlight) return;
      const ids = [...watched.current.keys()].filter((id) => !TERMINAL.has(statesRef.current[id]?.status ?? ''));
      if (!ids.length) return;
      inFlight = true;
      try {
        const results = await Promise.allSettled(ids.map(getDocumentIndexState));
        if (!stopped) setStates((current) => {
          const next = { ...current };
          results.forEach((result) => { if (result.status === 'fulfilled') next[result.value.nodeId] = result.value; });
          return next;
        });
      } finally { inFlight = false; }
    };
    poll();
    const timer = window.setInterval(poll, 3_000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, []);

  const value = useMemo(() => ({ states, watch }), [states, watch]);
  return <DocumentIndexingContext.Provider value={value}>{children}</DocumentIndexingContext.Provider>;
}

export function useDocumentIndexing() {
  const value = useContext(DocumentIndexingContext);
  if (!value) throw new Error('useDocumentIndexing must be used within DocumentIndexingProvider');
  return value;
}
